import { c as _c } from "react/compiler-runtime";
import { ConversationItem } from '@/api/generated/model';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { TooltipButton } from '@/comps/tooltip-button';
import { cn } from '@/utils/shad';
import { capitalize } from 'lodash';
import { Circle } from 'lucide-react';
export const getUserQuery = (conversation: ConversationItem[] | undefined) => {
  if (!conversation) return '';
  return conversation.filter(el => el.topic && !(el.topic as string ?? '').includes('greetings')).sort((a, b) => {
    const timestampA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    const timestampB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
    return timestampA - timestampB;
  })[0]?.user_query ?? '';
};
export const ConversationTopicsChip = t0 => {
  const $ = _c(20);
  const {
    topic,
    enableTooltip,
    children
  } = t0;
  if (topic === "") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className={cn("flex items-center gap-1.5 pr-2 font-medium", "rounded-[20px] border border-[#EC4C7266] py-0.5 pl-2", "border-[rgba(96,106,203,0.40)]", "w-max max-w-64")}><><div className="inline h-1.75 w-1.75 shrink-0 rounded-full bg-n-violet-platform text-n-violet-platform" /></><span className="line-clamp-1 text-foreground">None</span></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== children) {
      t2 = <div className="flex items-center">{t1}<div>{children}</div></div>;
      $[1] = children;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("flex items-center gap-1.5 pr-2 font-medium", "rounded-[20px] border border-[#EC4C7266] py-0.5 pl-2", "border-[rgba(96,106,203,0.40)]", "w-max max-w-64");
    t2 = <><div className="inline h-1.75 w-1.75 rounded-full bg-n-violet-platform text-n-violet-platform" /></>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== topic) {
    t3 = capitalize(topic);
    $[5] = topic;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== topic) {
    t4 = capitalize(topic);
    $[7] = topic;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = <span className="line-clamp-1 text-foreground">{t4}</span>;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== enableTooltip || $[12] !== t3 || $[13] !== t5) {
    t6 = <div className={t1}>{t2}<TooltipButton enabled={enableTooltip} content={t3}>{t5}</TooltipButton></div>;
    $[11] = enableTooltip;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== children) {
    t7 = <div>{children}</div>;
    $[15] = children;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <div className="flex items-center">{t6}{t7}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
const errorStatusMap: Record<string, {
  fg: string;
  bg: string;
  tooltip?: string;
}> = {
  successful: {
    fg: '#22c55e',
    bg: '#22c55e20',
    tooltip: 'The conversation completed without errors.\nThe AI agent answered effectively and the user reached their goal.'
  },
  problematic: {
    fg: '#f59e0b',
    bg: '#f59e0b20',
    tooltip: 'The conversation contained one or more errors,\nbut did not end with the user being dissatisfied.\nThe user continued despite the friction.'
  },
  abandoned: {
    fg: '#ef4444',
    bg: '#ef444420',
    tooltip: 'The conversation ended with an AI failure or user frustration.\nThe user left without a successful resolution.'
  },
  default: {
    fg: '#94a3b8',
    bg: '#94a3b820'
  }
};
export const getConversationErrorValueProps = (status: string) => {
  const normalized = status.toLowerCase().trim();
  return normalized in errorStatusMap ? errorStatusMap[normalized] : errorStatusMap.default;
};
export const ConversationErrorStatusBadge = t0 => {
  const $ = _c(18);
  const {
    status
  } = t0;
  if (!status) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span>-</span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== status) {
    t1 = getConversationErrorValueProps(status);
    $[1] = status;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const meta = t1;
  let t2;
  if ($[3] !== status) {
    t2 = capitalize(status);
    $[3] = status;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const label = t2;
  let t3;
  if ($[5] !== meta.bg) {
    t3 = {
      backgroundColor: meta.bg
    };
    $[5] = meta.bg;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== meta.fg) {
    t4 = <Circle size={8} fill={meta.fg} color={meta.fg} />;
    $[7] = meta.fg;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== label) {
    t5 = <span className="text-[12px] text-foreground">{label}</span>;
    $[9] = label;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="inline-flex w-max items-center gap-1 rounded-full px-2 py-0.5" style={t3}>{t4}{t5}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const content = t6;
  let t7;
  if ($[15] !== content || $[16] !== meta.tooltip) {
    t7 = meta.tooltip ? <Tooltip delayDuration={200}><TooltipTrigger>{content}</TooltipTrigger><TooltipContent>{meta.tooltip}</TooltipContent></Tooltip> : content;
    $[15] = content;
    $[16] = meta.tooltip;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};